import React, { useState } from "react";
import { LuMaximize2 } from "react-icons/lu";
import ProductLightbox from "./ProductLightbox";

export type Shot = {
  title: string;
  src: string;
  alt: string;
  width: number;
  height: number;
};

// React 18 passes unknown props through verbatim, so the attribute has to carry the HTML spelling
// rather than React 19's fetchPriority.
export const PRIORITY_ATTRS = { fetchpriority: "high" } as unknown as React.ImgHTMLAttributes<HTMLImageElement>;

const DOTS = ["#ff7e79", "#f4c75d", "#55cfc1"];

export default function ProductShot({
  shot,
  priority = false,
  aspect,
}: {
  shot: Shot;
  priority?: boolean;
  // Crops a tall capture to this width÷height from the top, so it cannot dwarf the copy beside it.
  aspect?: number;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="tw-relative">
      <figure className="tw-m-0 tw-overflow-hidden tw-rounded-[28px] tw-border tw-border-[#bfd6e8] tw-bg-white tw-p-3 tw-shadow-[0_24px_64px_rgba(23,72,111,.16)] sm:tw-rounded-[22px] sm:tw-p-2">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-t-[20px] tw-border-b tw-border-[#dbe7f0] tw-bg-[#f4f8fb] tw-px-4 tw-py-2.5 sm:tw-rounded-t-[15px] sm:tw-px-3">
          <div aria-hidden className="tw-flex tw-gap-1.5">
            {DOTS.map((colour) => (
              <span
                key={colour}
                className="tw-h-2 tw-w-2 tw-rounded-full"
                style={{ backgroundColor: colour }}
              />
            ))}
          </div>

          <p className="tw-m-0 tw-max-w-[68%] tw-truncate tw-text-xs tw-font-black tw-uppercase tw-tracking-[.11em] tw-text-[#57738c] sm:tw-text-[11px]">
            {shot.title}
          </p>

          <LuMaximize2 aria-hidden className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-[#087bd3]" />
        </div>

        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Enlarge ${shot.title} screenshot`}
          style={aspect ? { aspectRatio: String(aspect) } : undefined}
          className="tw-group tw-relative tw-block tw-w-full tw-cursor-zoom-in tw-overflow-hidden tw-rounded-b-[20px] tw-border-0 tw-bg-[#edf4f9] tw-p-0 tw-text-left focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-[#087bd3] sm:tw-rounded-b-[15px]"
        >
          <img
            src={shot.src}
            alt={shot.alt}
            width={shot.width}
            height={shot.height}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            {...(priority ? PRIORITY_ATTRS : null)}
            className={`tw-w-full tw-transition-transform tw-duration-300 group-hover:tw-scale-[1.012] ${
              aspect ? "tw-h-full tw-object-cover tw-object-top" : "tw-h-auto"
            }`}
          />
        </button>
      </figure>

      {open ? <ProductLightbox shot={shot} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}
